import './index.css';
import { api } from './services/api';
import { ClassItem } from './types';
import { renderSubmitView } from './views/SubmitView';
import { renderLibraryView } from './views/LibraryView';
import { renderAdminView } from './views/AdminView';
import { promptPassphrase } from './components/PassphraseModal';

class App {
  private appContainer: HTMLElement;
  private classSelect: HTMLSelectElement;
  private classes: ClassItem[] = [];
  private currentClass: ClassItem | null = null;

  constructor() {
    this.appContainer = document.getElementById('app')!;
    this.classSelect = document.getElementById('class-select') as HTMLSelectElement;

    this.init();
  }

  private async init() {
    // 授業一覧の取得
    this.classes = await api.getClasses();
    this.populateClassSelect();

    // ヘッダーナビゲーションの初期化
    this.bindHeaderEvents();

    // 初回ルーティング
    window.addEventListener('popstate', () => this.handleRoute());
    await this.handleRoute();
  }

  private populateClassSelect() {
    this.classSelect.innerHTML = '';
    this.classes.forEach((c) => {
      const opt = document.createElement('option');
      opt.value = c.id;
      opt.textContent = c.name;
      this.classSelect.appendChild(opt);
    });

    this.classSelect.addEventListener('change', () => {
      const selectedId = this.classSelect.value;
      if (selectedId) {
        this.navigateTo(`/c/${selectedId}/submit`);
      }
    });
  }

  private bindHeaderEvents() {
    document.getElementById('header-logo')?.addEventListener('click', () => {
      if (this.currentClass) {
        this.navigateTo(`/c/${this.currentClass.id}/submit`);
      } else if (this.classes.length > 0) {
        this.navigateTo(`/c/${this.classes[0].id}/submit`);
      } else {
        this.navigateTo('/public-library');
      }
    });

    document.getElementById('btn-public-library')?.addEventListener('click', () => {
      this.navigateTo('/public-library');
    });

    document.getElementById('btn-admin-mode')?.addEventListener('click', () => {
      this.navigateTo('/admin');
    });
  }

  public navigateTo(path: string) {
    window.history.pushState({}, '', path);
    this.handleRoute();
  }

  private async handleRoute() {
    const path = window.location.pathname;

    // 最新のクラス一覧を反映
    this.classes = await api.getClasses();

    // 1. 公開ライブラリ: /public-library
    if (path === '/public-library') {
      this.classSelect.style.display = 'none';
      await renderLibraryView(this.appContainer, {
        isPublicOnly: true,
        onNavigate: (p) => this.navigateTo(p)
      });
      return;
    }

    // 2. 管理モード: /admin
    if (path.startsWith('/admin')) {
      this.classSelect.style.display = 'none';
      await renderAdminView(this.appContainer, (p) => this.navigateTo(p));
      return;
    }

    // 3. 授業固有ルート: /c/{classId}/submit または /c/{classId}/library
    const classMatch = path.match(/^\/c\/([^/]+)\/(submit|library)/);
    let targetClassId = classMatch ? classMatch[1] : null;
    let targetView = classMatch ? classMatch[2] : 'submit';

    // ルートパス / または未指定の場合は先頭のクラスを採用
    if (!targetClassId) {
      if (this.classes.length > 0) {
        targetClassId = this.classes[0].id;
        targetView = 'submit';
        window.history.replaceState({}, '', `/c/${targetClassId}/submit`);
      } else {
        this.navigateTo('/public-library');
        return;
      }
    }

    const foundClass = this.classes.find((c) => c.id === targetClassId);
    if (!foundClass) {
      this.appContainer.innerHTML = `
        <div style="text-align: center; padding: 60px 20px;">
          <h2>⚠️ 授業が見つかりません</h2>
          <p style="color: var(--color-text-secondary); margin: 12px 0;">指定された授業ID「${targetClassId}」は存在しません。</p>
          <button class="btn btn-primary" id="btn-fallback-home">トップへ戻る</button>
        </div>
      `;
      this.appContainer.querySelector('#btn-fallback-home')?.addEventListener('click', () => {
        if (this.classes.length > 0) {
          this.navigateTo(`/c/${this.classes[0].id}/submit`);
        } else {
          this.navigateTo('/public-library');
        }
      });
      return;
    }

    this.currentClass = foundClass;
    this.classSelect.style.display = 'block';
    this.classSelect.value = this.currentClass.id;

    // パスフレーズ認証の確認
    const verified = await promptPassphrase(this.currentClass.id, this.currentClass.name);
    if (!verified) {
      // キャンセルまたは認証失敗時は公開ライブラリへフォールバック
      this.navigateTo('/public-library');
      return;
    }

    if (targetView === 'submit') {
      renderSubmitView(this.appContainer, this.currentClass, (p) => this.navigateTo(p));
    } else {
      await renderLibraryView(this.appContainer, {
        isPublicOnly: false,
        currentClass: this.currentClass,
        onNavigate: (p) => this.navigateTo(p)
      });
    }
  }
}

// アプリケーション起動
window.addEventListener('DOMContentLoaded', () => {
  new App();
});
